import type { SaberId } from './saberes.ts';
import type { NightmareType } from './nightmares.ts';
import { res, type Resources, type StructureType } from './structures.ts';

/**
 * The bastion from scratch (docs/CONSTRUCCION.md): one map with the keep alone on a height, terraced
 * ground, cliffs, rivers with their fords, lakes, woods, rock and ruins. Proof-of-concept numbers, to be
 * tuned by playing it.
 */
export const LAND = {
  /** Map radius in hexes (the rim fades into mist). */
  radius: 32,
  /** Height levels 0..top; the keep stands on the top one. */
  top: 3,
  /** The keep's plateau: every cell this close to it is on the top level. */
  crest: 3,
  /** Noise lattices (hexes apart) for the relief, cliffs and cover. */
  reliefStep: 7,
  coverStep: 5,
  /** How much the relief leans on distance to the keep against noise, in basis points (the rest is noise). */
  slopeBp: 6500,
  /** A step of one level is a cliff (no way up or down) where the cliff noise of the higher cell is above this (0–1023). */
  cliffAt: 560,
  /** Woods and rock outcrops where their noise is above these (0–1023), past `clear` hexes from the keep. */
  woodAt: 720,
  rockAt: 780,
  clear: 5,
  /** Ruins of the old garrison: chance per open cell within `ruinsWithin` hexes, in basis points. */
  ruinsBp: 1600,
  ruinsWithin: 9,
  /** Rivers: how many spring near the crest, and every how many cells a river leaves a ford. */
  rivers: 2,
  fordEvery: 6,
  /** Lakes fill level-0 hollows where the lake noise is above this (0–1023). */
  lakeAt: 760,
  /** Water never covers more than this share of the map, in basis points. */
  waterMaxBp: 1200,
  /** Crystal veins: how many open in the rock, how far from the keep, and how far from one another. */
  veins: 5,
  veinMin: 7,
  veinApart: 7,
  /** Ways up from the rim: how many zones nightmares climb from when no fissure can open. */
  zones: 3,
  /**
   * Fissures (2026-10-30): every wave cracks the ground open somewhere out in the field, this many hexes from
   * the keep, never inside the walls nor within `fissureClear` of anything built, and the nightmares climb out
   * of them. Nobody knows where before the wave comes; no two open closer than `fissureApart`.
   */
  fissureMin: 9,
  fissureMax: 16,
  fissureClear: 3,
  fissureApart: 7,
  /** Sets of fissures the general draws for a wave; it keeps the one that meets the bastion where it is weakest. */
  riftDraws: 6,
  /**
   * Fissures mid-wave (2026-11-03): heroes went to stand on the fissures and cut down each nightmare as it came
   * out. The general looks at the field every `thinkTicks`, at any moment of the wave; when heroes stand within
   * `campHexes` of a fissure that still has nightmares to let out (or now and then for no reason, `whimBp`), it
   * cracks a new one where no hero is within `heroClearHexes`, the towers reach least and the walk to the keep is
   * short, and what was coming out of the watched one comes out there instead. The ground trembles `warnTicks`
   * before it opens. With `decoyBp`, a second one trembles too, lets out `decoyOwed` and closes.
   */
  rifts: {
    thinkTicks: 24,
    campHexes: 4,
    heroClearHexes: 8,
    warnTicks: 48,
    cooldownTicks: 180,
    maxPerWave: 4,
    whimBp: 600,
    /** Least spawns still owed to the wave for the general to bother. */
    minOwed: 6,
    decoyBp: 4000,
    decoyOwed: 4,
    /** Weights in the spot's score: towers' fire on it (per point of fire per second), the walk to the keep (per hex). */
    fireW: 40,
    walkW: 6,
    /** Spots the general weighs per look (spread evenly over all of them, a fresh offset each look). */
    sample: 48,
  },
  /** Ground nightmares: steps to walk into a wood or a ford cell (open ground is 1), and extra steps to climb a level. */
  woodSteps: 3,
  fordSteps: 4,
  climbSteps: 2,
  /** A tower reaches this much farther per level it stands above the bottom (fixed, 1000 = 1 hex). */
  levelRange: 400,
  /** How a wall weighs on the way in, by kind of nightmare: breakers see it cheap, skirters dear. */
  wallClassBp: { break: 4000, normal: 10000, skirt: 25000 } as const,
};

export type WallClass = keyof typeof LAND.wallClassBp;

/** A campaign on terraced land starts with the keep alone: a store large enough to raise a palisade and the first towers. */
export const LAND_START_STORAGE_PER_PLAYER = res({ wood: 450, stone: 150, crystal: 30 });

/** Nightmares that would rather break a wall than walk round it, and those that would rather walk round. */
export const WALL_CLASS: Partial<Record<NightmareType, WallClass>> = {
  ram: 'break',
  ogre: 'break',
  giant: 'break',
  troll: 'break',
  beetle: 'break',
  goblin: 'skirt',
  wolf: 'skirt',
  slinger: 'skirt',
  archer: 'skirt',
};

/** What lies on a cell of the land (MapDef.land.kind). */
/** `ditch`: a trench dug by the group (an earthwork): walked slowly like a ford, never built on. */
export const GROUND = { open: 0, wood: 1, rock: 2, ruin: 3, water: 4, ford: 5, vein: 6, ditch: 7 } as const;
export type GroundKind = (typeof GROUND)[keyof typeof GROUND];

/**
 * Buildings that take three cells on terraced land (docs/CONSTRUCCION.md §3): a triangle, the cell picked and
 * two of its neighbours, all on one level and all free to build on. The rest take one.
 */
export const LAND_BIG: StructureType[] = ['forge', 'barracks', 'temple', 'workshop', 'lab', 'grove', 'hall', 'sanctum', 'depot', 'golemworks', 'trebuchet', 'foundry', 'arsenal', 'market', 'kennel', 'sieve'];
/** Seven-cell pieces on terraced land: the cell and the ring round it, all on one level. */
export const LAND_HUGE: StructureType[] = ['titanEye'];

/**
 * Golems that gather (docs/CONSTRUCCION.md §5). Each brings in `perDay` of its resource while it works the
 * nearest wood, rock or vein to a store (the keep or a gathering post); the trip costs `perHexBp` of that for
 * every hex past `nearHexes` from the store, never below `minBp`. A group of two with every golem gathering
 * brings in about a third of what its incursions give in a day (3 × 120 each).
 */
export const GATHER = {
  /** Iron only with a foundry standing, out of the rock, carried to the foundry (era II, docs/OBRAS.md). */
  perDay: { wood: 70, stone: 55, crystal: 20, iron: 6 },
  nearHexes: 3,
  perHexBp: 700,
  minBp: 2000,
};

/** What a golem gathers, and the ground it gathers it from. */
export const GATHER_KINDS = ['wood', 'stone', 'crystal', 'iron'] as const;
export type GatherKind = (typeof GATHER_KINDS)[number];
export const GATHER_GROUND: Record<GatherKind, number> = { wood: GROUND.wood, stone: GROUND.rock, crystal: GROUND.vein, iron: GROUND.rock };

/** The golem works: one more golem per level it stands at. */
export const GOLEMWORKS_PER_LEVEL = 1;

/**
 * What costs otherwise on terraced land: the wall starts as a timber palisade (wood, quick to raise round the
 * crest, docs/CONSTRUCCION.md §1) that the material upgrade turns to stone; the gate likewise.
 */
export const LAND_COST: Partial<Record<StructureType, Resources>> = {
  wall: res({ wood: 20 }),
  gate: res({ wood: 60, stone: 20 }),
};

/**
 * What each era brings on terraced land (docs/OBRAS.md). Only bastions on land read these; the ring keeps its
 * own rules.
 */
export const LAND_ERAS = {
  /** Structures that open earlier on land than their `minEra`: the trebuchet answers era II's mangonel. */
  minEra: { trebuchet: 2 } as Partial<Record<StructureType, number>>,
  /** A tower's count cap on land in a given era, over `eraTowerCap` (the trebuchet comes alone in era II). */
  capAt: { trebuchet: { 2: 1, 3: 2 }, arsenal: { 3: 1, 4: 2, 5: 2 }, rod: { 4: 3, 5: 4 }, gatetower: { 4: 6, 5: 8 }, boulder: { 1: 3, 2: 3, 3: 3, 4: 3, 5: 3 } } as Partial<Record<StructureType, Record<number, number>>>,
  /** The highest level by era (index: era − 1): towers and traps. */
  towerTop: [3, 4, 4, 5, 6] as readonly number[],
  trapTop: [3, 4, 4, 5, 5] as readonly number[],
  /**
   * Without an active artificer in the group, levels 3 and up cost this much. Active: a player who played in the last
   * `artificerDays` with an artificer of at least `artificerLevel`, in play or on the bench (a fresh one made for the
   * occasion doesn't count).
   */
  noArtificerBp: 15000,
  artificerDays: 4,
  artificerLevel: 8,
};

/**
 * On terraced land a building's level changes what it does, not only its HP (docs/OBRAS.md §4). Index: level − 1.
 * Bp values multiply the building's usual effect (10000 = as today); `add` values add to it.
 */
export const LAND_LEVELS = {
  temple: { range: [2500, 3200, 3200], towerBp: [11500, 11500, 12500] },
  grove: { woodBp: [10000, 15000, 15000], slowAddBp: [0, 0, 1000] },
  lab: { elixirs: [1, 2, 2], elixirBp: [10000, 10000, 13000] },
  workshop: { clockworkAdd: [0, 1, 1], overloadRestBp: [10000, 10000, 6500] },
  hall: { maxTrophies: [5, 7, 7], drumsBp: [10000, 10000, 15000] },
  watchtower: { rangeBp: [10000, 13000, 13000], revealBp: [10000, 10000, 13000] },
  sanctum: { healBp: [10000, 13000, 13000], blessBp: [10000, 10000, 15000] },
  /** A gathering post's reach: hexes taken off every trip from it. */
  depot: { reach: [0, 2, 4] },
};

/**
 * Tower branches on terraced land (docs/OBRAS.md §3): a level 4 tower picks one of two with a job; it can be
 * changed later by paying the other in full. Balista, catapult and arcane branch in era III; clockwork and
 * trebuchet in era IV.
 */
export type BranchId = 'repeater' | 'harpoon' | 'pitch' | 'counterweight' | 'focus' | 'sky' | 'steam' | 'sealed' | 'split' | 'embers' | 'whistler' | 'lime' | 'drowse';
export const BRANCH_IDS: BranchId[] = ['repeater', 'harpoon', 'pitch', 'counterweight', 'focus', 'sky', 'steam', 'sealed', 'split', 'embers', 'whistler', 'lime', 'drowse'];
export interface BranchDef {
  tower: StructureType;
  era: number;
  /** Shots per volley, each at `damageBp` (repeater). */
  shots?: number;
  damageBp?: number;
  /** More reach, and the reload stretched by `reloadBp`. */
  rangeAdd?: number;
  reloadBp?: number;
  /** Harpoon: every nightmare on the line to the target (and past it, to the tower's reach) within `width` is hit. */
  line?: { width: number; bigBp: number };
  /** Burning ground where the shot lands (one patch per tower, refreshed): `dmgBp` of the shot every `every` for `ticks`. */
  burn?: { radius: number; ticks: number; every: number; dmgBp: number };
  /** Sky: shoots only what flies, as this damage type. */
  airOnly?: boolean;
  dmgType?: 'holy' | 'fire' | 'physical';
  /** Steam: a blast round the target. */
  splash?: number;
  /** Sealed: nothing puts it to sleep, cocoons, curses or turns it. */
  sealed?: boolean;
  /** Split stones: this many more blows at `spread` round the first, each `damageBp`. */
  split?: { n: number; spread: number; damageBp: number };
  /**
   * Another way to build the tower, opened by a saber (docs/SABERES.md §4.2): picked when it is laid, never taken or
   * left at level 4, and it takes no level-4 branch. `saber` names it.
   */
  ingenio?: SaberId;
  /** The whistling ballista: what it hits on foot runs away (WHISTLER). */
  flee?: boolean;
  /** The lime catapult: a cloud where it lands, where those who shoot from afar can't (LIME). */
  lime?: boolean;
  /** The drowsing tower: one shot in DROWSE.every puts its target to sleep instead (DROWSE). */
  drowse?: boolean;
}
export const BRANCHES: Record<BranchId, BranchDef> = {
  repeater: { tower: 'ballista', era: 3, shots: 2, damageBp: 5500 },
  harpoon: { tower: 'ballista', era: 3, line: { width: 450, bigBp: 13000 } },
  pitch: { tower: 'catapult', era: 3, burn: { radius: 1100, ticks: 60, every: 12, dmgBp: 2200 } },
  counterweight: { tower: 'catapult', era: 3, rangeAdd: 1500, reloadBp: 12000 },
  focus: { tower: 'arcane', era: 3, damageBp: 15000, rangeAdd: 800 },
  sky: { tower: 'arcane', era: 3, airOnly: true, damageBp: 20000, rangeAdd: 1000, dmgType: 'holy' },
  steam: { tower: 'clockwork', era: 4, splash: 1000, damageBp: 9000 },
  sealed: { tower: 'clockwork', era: 4, sealed: true },
  split: { tower: 'trebuchet', era: 4, split: { n: 2, spread: 1300, damageBp: 5000 } },
  embers: { tower: 'trebuchet', era: 4, burn: { radius: 1500, ticks: 72, every: 12, dmgBp: 1500 } },
  /** A saber's (docs/SABERES.md §4.2): the whistling ballista. */
  whistler: { tower: 'ballista', era: 1, ingenio: 'whistler', damageBp: 6000, flee: true },
  lime: { tower: 'catapult', era: 2, ingenio: 'lime', damageBp: 5000, lime: true },
  drowse: { tower: 'arcane', era: 1, ingenio: 'drowse', damageBp: 5000, drowse: true },
};
/** The branches a level 4 tower picks from: not the ingenios, which are chosen when a tower is laid. */
export const LEVEL_BRANCH_IDS: BranchId[] = BRANCH_IDS.filter((b) => !BRANCHES[b].ingenio);
/** The ingenios: branches a saber opens, chosen when the tower is laid (docs/SABERES.md §4.2). */
export const ENGINE_BRANCH_IDS: BranchId[] = BRANCH_IDS.filter((b) => !!BRANCHES[b].ingenio);
/** What picking a branch costs (bp of the tower's level 4 price) and how long its job takes. */
export const BRANCH_COST_BP = 6000;
export const BRANCH_TIME_HOURS = 6;

/**
 * Earthworks (docs/OBRAS.md §5): jobs that change the land itself. Clear: wood → open; quarry: rock → open; raise
 * and lower: a level up or down; ditch: a slow trench; ford: deep water by the bank → ford.
 */
export type EarthId = 'clear' | 'quarry' | 'raise' | 'lower' | 'ditch' | 'ford';
export const EARTH_IDS: EarthId[] = ['clear', 'quarry', 'raise', 'lower', 'ditch', 'ford'];

/** The adarve (era III, terraced land): which towers mount on a stone or iron wall run, and at what level. */
export const MOUNTS: StructureType[] = ['ballista', 'arcane', 'clockwork'];
export const MOUNT_ERA = 3;
export const MOUNT_LEVEL = 3;

/**
 * The Titan's Eye (era V, terraced land): every `every` ticks it gathers one charge for each nightmare within its
 * reach (STRUCTURES.titanEye.range); at `full` it fires by itself at the biggest one in reach, a blow of `damage`
 * that no plate or scale turns.
 */
export const TITAN_EYE = { every: 12, full: 90, damage: 1600 };

/** Earthworks (docs/OBRAS.md §5): the era each opens in, what it costs and gives, how long the golems take. */
export const LAND_EARTH = {
  era: { clear: 1, quarry: 2, raise: 3, lower: 3, ditch: 3, ford: 4 } as Record<EarthId, number>,
  cost: {
    clear: res({}),
    quarry: res({}),
    raise: res({ stone: 40, wood: 20 }),
    lower: res({ wood: 20 }),
    ditch: res({ wood: 30 }),
    ford: res({ stone: 60 }),
  } as Record<EarthId, Resources>,
  /** What it leaves in the store when done: a cleared wood some timber, quarried rock some stone (less than a few days of golems there). */
  give: { clear: res({ wood: 25 }), quarry: res({ stone: 25 }), raise: res({}), lower: res({ stone: 10 }), ditch: res({}), ford: res({}) } as Record<EarthId, Resources>,
  hours: { clear: 2, quarry: 3, raise: 4, lower: 3, ditch: 2, ford: 4 } as Record<EarthId, number>,
  /** How many cells the bastion may have changed in all, by era (index: era − 1); ditches have their own cap inside it. */
  cap: [12, 12, 24, 36, 36] as readonly number[],
  ditchCap: 12,
};

/** The arsenal (era III, terraced land): ballistas and catapults touching it fire this much faster (bp); with a beacon too, the two together stop at `capBp`. */
export const ARSENAL = { fireRateBp: 2500, towers: ['ballista', 'catapult'] as StructureType[], capBp: 4000 };
